import React from 'react';
import { IBlocks } from './IBlocks';
import ProductReference from './ProductReference';
import Hero from '../ui/Hero/Hero';
import Questions from './Questions';
import GraphicDesign from './GraphicDesign';
import Text from './Text';
import Section from '../ui/Section/Section';
import Image from './Image';
import ImageText from './ImageText';
import Benefits from './Benefits';
import ButtonLink from '../ui/Buttons/ButtonLink';
import { graphql } from 'gatsby';
import FallbeispieleReferences from './FallbeispieleReferences';
import ZubehoerReference from './ZubehoerReference';
import './Blocks.scss';
import StepToStep from './StepToStep';
import Collapse from '../ui/Collapse/Collapse';
import {kebabCase} from 'lodash';
import AnchorMenu from './AnchorMenu';
import classnames from 'classnames';
import ContentReferences from './ContentReferences';
import * as styles from './Blocks.module.scss';
import Presentation from './Presentation';
import BlockProducts from './BlockProductDisplay';

type Props = {
    blocks: IBlocks
}

export default (props: Props) => {
    const { blocks } = props;

    if (!blocks) {
        return null;
    }

    let blockElements: Array<any> = [];

    let key: number = 0;
    for (const block of blocks) {

        const options = (block.options) ? block.options : {};


        // extract title
        let title = block.title ? <h2 className="title has-hyphens">{block.title}</h2> : "";

        // extract wrapper
        let wrapperProps = {
            id: block.options?.anchorTitle ? kebabCase(block.options.anchorTitle) : block.title ? kebabCase(block.title) : undefined,
            className: classnames([
                block?.options?.hiddenOn ? block.options.hiddenOn.map(hiddenOn => "is-hidden-" + hiddenOn + (hiddenOn === "mobile" ? "" : "-only")) : "",
            ]),
            hasTitle: block.title ? true : false,
        };
        let Wrapper: any = (props) => <Section {...wrapperProps} {...props} size={(options.size) ? options.size : undefined}>{props.children}</Section>;
        switch (block.type) {
            case "hero":
                Wrapper = ({children}) => <Hero {...wrapperProps} size={(options.size) ? options.size : undefined}>{children}</Hero>;
                break;
            case "collapse":
                Wrapper = ({children, collapseProps}) => <Section id={kebabCase(block.title)}  size={options.size} className={classnames(["collapse", styles.collapseWrapper])} hasTitle={wrapperProps.hasTitle}><Collapse {...wrapperProps} {...collapseProps} title={block.title}>{children}</Collapse> </Section>;
                title = null;
                break;
        }



        // Load widgets for each block
        if (block.productReferences) {
            blockElements.push(
                    <ProductReference
                    {...wrapperProps} 
                    title={block.productReferences.title}
                    text={block.productReferences.text}
                    pathToProductList={block.productReferences.pathToProductList}
                    references={block.productReferences.references}
                />
                )
                continue;
        }
        
        if (block.zubehoerReferences) {
            blockElements.push(
                    <ZubehoerReference
                    {...wrapperProps} 
                    pathToProductList={block.zubehoerReferences.pathToProductList}
                    references={block.zubehoerReferences.references}
                />
                )
                continue;
        }

        if (block.fallbeispielReferences) {
            blockElements.push(
                <FallbeispieleReferences
                    {...wrapperProps} 
                    title={block.fallbeispielReferences.title}
                    references={block.fallbeispielReferences.references}
                />
            )
            continue;
        }

        if (block.contentReferences) {
            blockElements.push(
                <ContentReferences
                    {...wrapperProps} 
                    title={block.contentReferences.title ? block.contentReferences.title : block.title}
                    references={block.contentReferences.references}
                />
            )
            continue;
        }
       
        if (block.anchorMenu) {
            blockElements.push(
                    <Wrapper className="is-hidden-touch" style={{marginTop: "3rem"}}><AnchorMenu blocks={blocks} /></Wrapper>
            )
            continue;
        }


        if (block.questions) {
            blockElements.push(
                <Questions 
                    {...wrapperProps} 
                    key={"block" + key} 
                    bgAfter={block.questions.hasOwnProperty("bgAfter") ? block.questions.bgAfter : undefined}
                    bgBefore={block.questions.hasOwnProperty("bgBefore") ? block.questions.bgBefore : undefined}
                />
            );
            continue;
        }

        if (block.graphicDesign) {
            blockElements.push(<GraphicDesign text={block.text} title={block.title} key={"block" + key} />);
            continue;
        }


        // Image and Text together
        if (block.image && block.text) {
            if (block.type === "hero") {
                blockElements.push(<Wrapper>
                    <div className="columns">
                        <div className="column">
                            <div className="content content-small">
                                {title}
                                <Text>{block.text}</Text>
                                {block.link && <ButtonLink to={"/" + block.link.to} variation="minimal">{block.link.text}</ButtonLink>}
                            </div>
                        </div>
                        <div className={classnames(["column is-one-quarter", "is-hidden-mobile", styles.illustration])}>
                            <Image src={block.image.src} alt={block.image.alt} ratio={block.image.ratio} title={block.image.title} />
                        </div>
                    </div>
                </Wrapper>); 
            } else {
                blockElements.push(<Wrapper><ImageText image={block.image} >
                    {title}
                    <div dangerouslySetInnerHTML={{__html: block.text}}></div>
                    {block.link && <ButtonLink to={"/" + block.link.to}>{block.link.text}</ButtonLink>}
                    </ImageText></Wrapper>); 
            }
            continue;
        }


        // Products with text
        if (block.products && block.text) {
            blockElements.push(<Wrapper>

                {/* Layout for one product */}
                {block.products.length === 1 && <div className="columns is-vcentered">
                    <div className="column">
                        <Text>
                                {title}
                                {block.text}
                        </Text>
                    </div>
                    <div className="column is-hidden-touch is-1"></div>
                    <div className="column is-one-third">
                        <BlockProducts products={block.products} columnSize="is-full" />
                    </div>
                </div>}

                {/* Layout for more then one product */}
                {block.products.length > 1 && <>
                    <Text>
                        {title}
                        {block.text}
                    </Text>
                    <BlockProducts products={block.products} />
                </>}
            </Wrapper>);
             continue;
        }

        // Text only
        if (block.text && !block.image && !block.products) {
            blockElements.push(<Wrapper>
                <Text>
                        {title}
                        {block.text}
                </Text>
                {block.link && <ButtonLink to={"/" + block.link.to}>{block.link.text}</ButtonLink>}
                </Wrapper>);
                 continue;
        }

        // Image only
        if (block.image && !block.text) {
            blockElements.push(<Wrapper><Image src={block.image.src} alt={block.image.alt} title={block.image.title} ratio={block.image.ratio} /></Wrapper>); 
            continue;
        }

        // Products only
        if (block.products && !block.text) {
            blockElements.push(<Wrapper>
                <BlockProducts products={block.products} />
            </Wrapper>);
             continue;
        }


        if (block.benefits) {
            blockElements.push(<Wrapper><Benefits {...block.benefits} /></Wrapper>); 
            continue;
        }

        if (block.stepToStep) {
            blockElements.push(<Wrapper><StepToStep {...block.stepToStep} /></Wrapper>)
            continue;
        }

        if (block.presentation) {
            blockElements.push(<Wrapper collapseProps={{showTitleOnlyOnBehaviour: true}}><Presentation {...block.presentation} /></Wrapper>)
            continue;
        }


        key++;
    }


    return <div className="blocks">
        {blockElements.map((item, index) => <React.Fragment key={index}>{item}</React.Fragment>)}
    </div>
}


export const blocks = graphql`fragment BlocksFragment on Blocks {
  options {
    size
    hiddenOn
    anchorMenu
    anchorTitle
  }
  type
  questions {
    bgBefore
    bgAfter
  }
  productReferences {
    pathToProductList
    references {
      url
      listenbild {
        alt
        src {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      name
      kurzbeschreibung
    }
    title
    text
  }
  products {
    product {
      name
      kurzbeschreibung
      url
      listenbild {
        alt
        src {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      produktTyp
      productWidgetKey
      filters {
        typ
        wert
        filter
        widget
        widgetType
        defaultValue
      }
    }
    name
    kurzbeschreibung
    url
    listenbild {
      alt
      src {
        childImageSharp {
          gatsbyImageData
        }
      }
    }
    produktTyp
    productWidgetKey
    filters {
      typ
      wert
      filter
      widget
      widgetType
      defaultValue
    }
  }
  zubehoerReferences {
    pathToProductList
    references {
      url
      listenbild {
        alt
        src {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      name
      kurzbeschreibung
    }
  }
  fallbeispielReferences {
    title
    references {
      url
      listenbild {
        alt
        src {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      name
      kurzbeschreibung
    }
  }
  contentReferences {
    title
    references {
      title
      kurzbeschreibung
      fallbeispiel {
        kurzbeschreibung
        url
        listenbild {
          alt
          src {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        name
      }
      material {
        kurzbeschreibung: description
        url
        listenbild {
          alt
          src {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        name
      }
      druckverfahren {
        kurzbeschreibung: description
        url
        listenbild {
          alt
          src {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        name
      }
      weiterverarbeitung {
        kurzbeschreibung: description
        url
        listenbild {
          alt
          src {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        name
      }
      produkt {
        kurzbeschreibung
        url
        listenbild {
          alt
          src {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        name
      }
      landingpage {
        kurzbeschreibung: description
        url
        listenbild {
          alt
          src {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        name
      }
      video {
        kurzbeschreibung
        url
        listenbild {
          alt
          src {
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        name
        video
      }
    }
  }
  image {
    src {
      childImageSharp {
        gatsbyImageData
      }
    }
    alt
    title
    ratio
  }
  anchorMenu
  title
  text
  graphicDesign
  benefits {
    points
    image {
      src {
        childImageSharp {
          gatsbyImageData
        }
      }
      alt
      title
    }
  }
  link {
    to
    text
  }
  stepToStep {
    title
    steps {
      title
      text
      images {
        src {
          childImageSharp {
            gatsbyImageData
          }
        }
        alt
        title
      }
      button {
        to
        text
      }
    }
  }
  presentation {
    text
    images {
      src {
        childImageSharp {
          gatsbyImageData
        }
      }
      alt
      title
    }
  }
}
`